import type { Asset, UnresolvedLink } from 'contentful';
import { normalizeContentfulAssetUrl } from 'libs/contentful/rest/adapter/transforms';
import type { MediaAsset } from 'libs/contentful/types';

/**
 * Flattens a resolved Contentful `Asset` (the raw Entry-native shape) into the
 * flat `MediaAsset` that `<Media>` consumes. Entry-native modules should route
 * every asset field they pass to `<Media>` through this.
 *
 * Returns undefined for unresolvable links: the REST client's default chain
 * leaves links to archived/unpublished assets as raw Link objects rather than
 * removing them, so `fields` is not guaranteed to exist at runtime.
 */
export function toMediaAsset(
  asset: Asset<'WITHOUT_UNRESOLVABLE_LINKS'> | UnresolvedLink<'Asset'>,
): MediaAsset | undefined {
  if (!('fields' in asset)) return undefined;

  const { fields, sys } = asset;
  const file = fields.file;

  return {
    sys: { id: sys.id },
    url: file?.url ? normalizeContentfulAssetUrl(file.url) : '',
    contentType: file?.contentType ?? '',
    title: fields.title ?? '',
    description: fields.description,
    fileName: file?.fileName,
    size: file?.details?.size,
    width: file?.details?.image?.width,
    height: file?.details?.image?.height,
  };
}
